import * as React from 'react';

import moment from 'moment';
import { tl } from 'common/services/i18nService';
import { SessionState } from 'common/store/session/reducer';
import {
    Breadcrumb, Button, Header, Icon, Popup, SemanticCOLORS,
} from 'semantic-ui-react';

import * as styles from 'common/components/header/Header.scss';
import { isNullOrUndefined } from 'common/util/utils';
import { SIMPLE_DATE_FORMAT_STRING_NO_SECONDS } from 'common/store/common/constants';
import { hasAccessToEventLogsForUser } from 'common/services/permissions/commonPermissionService';
import * as gradingStyles from './Header.scss';
import { checkFirstTimeViewLocalStorage } from './services/checkFirstTimeLocalStorage';
import navigationListService from '../../services/navigationListService';
import { ContextState } from '../../store/context/reducer';
import { AssessmentState } from '../../store/assessment/reducer';
import { onMarkSchemeFileUploaded, onToggleConfirmModal } from '../../store/optionsMenu/actions';

import {
    ModerationStatus, WorkflowPrimaryActions, WorkflowState,
} from '../../store/context/types';
import { isCurrentUserEvaluatorInSelectedCommittee } from '../../services/committeeService';
import OptionsMenuButton from './components/OptionsMenuButton';
import store from '../../store';
import { onPrimaryActionClick, updateGradingInfo } from '../../store/assessment/actions';
import { OptionsMenuActionsName } from '../../store/optionsMenu/types';
import { getGradingWorkflowInfo } from '../../store/context/actions';
import {
    getGradingLevelForTestOrCommittee,
    getMarkingStatusForGradingWorkflow,
    getProgressStatusForGradingWorkflow,
    getMarkingStatus,
    getMarksModeForTestOrCommittee,
    GRADING_LEVEL,
    GRADING_MODE, isGraderThatStillHasNotGradedCandidates,
} from '../../services/workflowService';

import CoGradersList from './components/CoGradersList';
import { ExternalInfoButton } from './components/externalSystem/ExternalInfoButton';

import {
    hasCurrentUserEvaluatorPrivilege, hasCurrentUserPlannerPrivilege, shouldRestrictVisibilityOfGraderInfo,
} from '../../services/permissions/PermissionService';
import { getCountdownCaption, getFormattedDuration } from '../../util/gradingUtils';
import { ExternalSystem } from '../../store/common/constants';
import { getPartyCommitteeIdForContext } from '../../services/gradingPartyService';
import { getOptionsMenuItems, MenuItemAction, MenuItemsGroup } from '../../services/optionsMenuItemsService';
import UserIconButton from './UserIconButton';

interface HeaderRightProps {
    context?: ContextState;
    assessment?: AssessmentState;
    session?: SessionState;
    setLanguage?: (language: string) => void;
    openIssuesOverlay?: (nIssues: number) => void;
    onToggleDetailsPanel?: () => void;
}

interface HeaderRightState {
    isMenuOpen: boolean;
    canPopUpOpen: boolean;
    currentWorkflowState?: string;
    remainingTimeCaption?: string;
    remainingExtraTimeOrIddDurationCaption?: string;
}

function getColor(status: string): SemanticCOLORS {
    switch (status) {
    case 'graded':
        return 'green' as SemanticCOLORS;
    case 'grading_in_progress':
        return 'blue' as SemanticCOLORS;
    default:
        return 'grey' as SemanticCOLORS;
    }
}

class HeaderRight extends React.Component<HeaderRightProps, HeaderRightState> {
    closesInCountdownIntervalId: number;
    extraTimeOrIddIntervalId: number;

    constructor(props: HeaderRightProps) {
        super(props);

        this.state = {
            isMenuOpen: false,
            canPopUpOpen: true,
        };
    }

    onToggleMenu = () => {
        const { isMenuOpen } = this.state;
        this.setState({
            isMenuOpen: !isMenuOpen,
        });
    };

    componentDidMount(): void {
        const { assessment } = this.props;
        this.checkStateForCountdown();
        if (assessment.gradingInstructionsContentItemId) {
            store.dispatch(
                onMarkSchemeFileUploaded({
                    title: assessment.gradingInstructionsTitle,
                    contentItemId: assessment.gradingInstructionsContentItemId,
                    extension: assessment.gradingInstructionsExtension,
                })
            );
        }
    }

    componentDidUpdate(
        prevProps: Readonly<HeaderRightProps>,
        prevState: Readonly<HeaderRightState>
    ): void {
        const { currentWorkflowState } = this.state;

        if (prevState.currentWorkflowState !== currentWorkflowState) {
            this.checkStateForCountdown();
        }
    }

    static getDerivedStateFromProps(
        nextProps: Readonly<HeaderRightProps>,
        prevState: HeaderRightState
    ) {
        const { assessment, context } = nextProps;
        const { workflowInfo } = context;
        let currentWorkflowState;
        if (
            workflowInfo
            && (workflowInfo.evaluatorState !== prevState.currentWorkflowState
                || workflowInfo.plannerState !== prevState.currentWorkflowState)) {
            if (!assessment.committees) {
                const hasEvaluatorPermission = hasCurrentUserEvaluatorPrivilege();
                if (hasEvaluatorPermission) {
                    currentWorkflowState = workflowInfo.evaluatorState;
                } else {
                    currentWorkflowState = workflowInfo.plannerState;
                }
            } else if (isCurrentUserEvaluatorInSelectedCommittee()) {
                currentWorkflowState = workflowInfo.evaluatorState;
            } else {
                currentWorkflowState = workflowInfo.plannerState;
            }
            return {
                currentWorkflowState,
            };
        }

        // preserve old state
        return null;
    }

    checkStateForCountdown = () => {
        const { currentWorkflowState } = this.state;
        if (currentWorkflowState) {
            if (
                currentWorkflowState === WorkflowState.NOT_FINISHED
                && !this.closesInCountdownIntervalId
            ) {
                this.setState(
                    {
                        remainingTimeCaption: this.getCountdown(),
                    },
                    () => {
                        this.closesInCountdownIntervalId = window.setInterval(
                            () => {
                                this.setState({
                                    remainingTimeCaption: this.getCountdown(),
                                });
                            },
                            1000
                        );
                    }
                );
            }
            if (currentWorkflowState !== WorkflowState.NOT_FINISHED
                && currentWorkflowState !== WorkflowState.GRADING_COMPLETED) {
                const { workflowInfo } = this.props.context;
                const {
                    numberOfNotSubmittedCandidates,
                    longestDeliveryDeadlineWithExtraTime,
                } = workflowInfo;
                const longestMoment = moment.utc(longestDeliveryDeadlineWithExtraTime, SIMPLE_DATE_FORMAT_STRING_NO_SECONDS);
                if (numberOfNotSubmittedCandidates > 0 && longestMoment.local().isAfter(moment())) {
                    this.setState(
                        {
                            remainingExtraTimeOrIddDurationCaption: this.getExtraTimeOrIddCountdown(),
                        },
                        () => {
                            this.extraTimeOrIddIntervalId = window.setInterval(
                                () => {
                                    this.setState({
                                        remainingExtraTimeOrIddDurationCaption: this.getExtraTimeOrIddCountdown(),
                                    });
                                },
                                1000
                            );
                        }
                    );
                }
            }
        }
    };

    componentWillUnmount(): void {
        // in case this unloads before the interval is cleared
        clearInterval(this.closesInCountdownIntervalId);
        clearInterval(this.extraTimeOrIddIntervalId);
    }

    onClose = () => {
        this.setState({
            isMenuOpen: false,
        });
    };

    onOpen = () => {
        this.setState({
            isMenuOpen: true,
        });
    };

    getCountdown = () => {
        const { assessment, session } = this.props;
        const { assessmentRunEndTime, maxExtraTime } = assessment;
        const endTimeMoment = moment.utc(assessmentRunEndTime, SIMPLE_DATE_FORMAT_STRING_NO_SECONDS);
        const diff = moment().diff(
            endTimeMoment.local()
        );
        if (diff >= 0) {
            window.clearInterval(this.closesInCountdownIntervalId);
            this.closesInCountdownIntervalId = null;
            // reload workflow data
            store.dispatch(getGradingWorkflowInfo());
            store.dispatch(updateGradingInfo(session.user.id));
            return tl('grading.header.progress.refreshing');
        }
        return getCountdownCaption(endTimeMoment.local(), maxExtraTime);
    };

    getProgressStatus = () => {
        const { assessment, context, session } = this.props;
        const { workflowInfo } = context;
        const { language } = session;
        const { isGradingWorkflowsEnabledForTest } = assessment;
        const langCode = language.split('-')[0] || 'en';
        const selectedCommitteeId = getPartyCommitteeIdForContext(context.partyState.gradingPartyDocument);

        if (isGradingWorkflowsEnabledForTest) {
            const workflowProgressStatus = getProgressStatusForGradingWorkflow(
                this.state.currentWorkflowState, langCode, this.state.remainingTimeCaption
            );
            if (workflowProgressStatus !== undefined) {
                return workflowProgressStatus;
            }
        }

        return this.getNonGradingWorkflowProgressStatus(
            workflowInfo,
            langCode,
            selectedCommitteeId
        );
    };

    private readonly getNonGradingWorkflowProgressStatus = (
        workflowInfo: ContextState['workflowInfo'],
        langCode: string,
        selectedCommitteeId: number
    ) => {
        switch (this.state.currentWorkflowState) {
        case WorkflowState.NOT_FINISHED:
            return this.state.remainingTimeCaption;
        case WorkflowState.NOT_READY_FOR_GRADING:
            // TODO - graders will not see issues for now, review later
            if (!hasCurrentUserPlannerPrivilege()) {
                return null;
            }
            return tl(
                `grading.header.progress.notReadyForGradingIssues.${workflowInfo.firstNotReadyForGradingIssue}`
            );
        case WorkflowState.SINGLE_GRADER:
        case WorkflowState.READY_FOR_GRADING:
        case WorkflowState.OWN_GRADING_STARTED:
            return tl('grading.header.progress.gradingProgress',
                {
                    gradedCandidatesCount: workflowInfo.numberOfGradedCandidatesForContext,
                    totalCandidatesCount: workflowInfo.numberOfCandidatesForContext,
                });
        case WorkflowState.OWN_GRADING_CONFIRMED_WAITING:
            return this.renderOwnGradingConfirmedWaiting(workflowInfo);
        case WorkflowState.FINAL_GRADING_CONFIRMED_WAITING:
            return this.renderFinalGradingConfirmedWaiting(workflowInfo);
        case WorkflowState.FINAL_GRADING_CONFLICTS:
            return this.renderFinalGradingConflicts(workflowInfo);
        case WorkflowState.FINAL_GRADING_ALLOWED:
            return this.renderFinalGradingAllowed(workflowInfo, selectedCommitteeId);
        case WorkflowState.GRADING_COMPLETED:
            return this.renderGradingCompleted(workflowInfo, langCode);
        default:
            return '';
        }
    };

    private readonly renderOwnGradingConfirmedWaiting = (workflowInfo: ContextState['workflowInfo']) => {
        const text = tl('grading.header.progress.awaitingCoGraders', {
            evaluatorsRemaining: workflowInfo.numberOfEvaluatorsWaitingFor,
        });

        return (
            <Popup
                content={(
                    <CoGradersList
                        graders={workflowInfo.evaluatorsWaitingFor}
                        worflowStateText={tl('grading.header.hasNotShared')}
                    />
                )}
                hoverable
                flowing
                position="bottom right"
                trigger={<div>{text}</div>}
                style={{ padding: 0 }}
            />
        );
    };

    private readonly renderFinalGradingConfirmedWaiting = (workflowInfo: ContextState['workflowInfo']) => {
        const text = tl('grading.header.progress.awaitingCoGraders', {
            evaluatorsRemaining: workflowInfo.numberOfEvaluatorsWaitingFor,
        });

        return (
            <Popup
                content={(
                    <CoGradersList
                        graders={workflowInfo.evaluatorsWaitingFor}
                        worflowStateText={tl('grading.header.hasNotConfirmed')}
                    />
                )}
                hoverable
                flowing
                position="bottom right"
                trigger={<div>{text}</div>}
                style={{ padding: 0 }}
            />
        );
    };

    private readonly renderFinalGradingConflicts = (workflowInfo: ContextState['workflowInfo']) => {
        const nrOfConflicts = workflowInfo
            ? workflowInfo.numberOfCandidatesWithConflict
            : 0;
        const remainingCandidates = workflowInfo.numberOfRemainingCandidatesToGrade;
        if (remainingCandidates && remainingCandidates > 0) {
            return `${remainingCandidates} ${tl(
                'grading.header.progress.remainingCandidates'
            )}`;
        }
        return tl('grading.header.progress.finalGradingConflicts', {
            nrOfConflicts,
        });
    };

    private readonly renderFinalGradingAllowed = (workflowInfo: ContextState['workflowInfo'], selectedCommitteeId: number) => {
        const marksMode = getMarksModeForTestOrCommittee(selectedCommitteeId);
        if (marksMode === GRADING_MODE.COLLABORATIVE) {
            const percentageCompleted = workflowInfo
                    && workflowInfo.numberOfCandidatesForContext > 0
                ? Math.floor(
                    (workflowInfo.numberOfGradedCandidatesForContext
                                  / workflowInfo.numberOfCandidatesForContext)
                                  * 100
                )
                : 0;
            return tl(
                'grading.header.progress.gradingStartedProgress',
                {
                    percentageCompleted,
                }
            );
        }
        return tl('grading.header.progress.finalGradingAllowed');
    };

    private readonly renderGradingCompleted = (workflowInfo: ContextState['workflowInfo'], langCode: string) => {
        const confirmationMoment = moment.utc(
            workflowInfo.finalConfirmationDate,
            SIMPLE_DATE_FORMAT_STRING_NO_SECONDS
        );
        const fromConfirm = confirmationMoment.isValid()
            ? confirmationMoment.locale(langCode).fromNow()
            : '';
        return tl('grading.header.progress.gradingCompletedTimestamp', {
            confirmationDate: fromConfirm,
        });
    };

    openCandidateLevelHelp = () => {
        window.open(tl('individual.confirmation.helpLink'));
    };

    renderSecondaryStatus = () => {
        const { assessment, context } = this.props;
        const selectedCommitteeId = getPartyCommitteeIdForContext(context.partyState.gradingPartyDocument);
        let secondaryStatus;
        const candidateLevelGrading: boolean = getGradingLevelForTestOrCommittee(selectedCommitteeId)
            === GRADING_LEVEL.CANDIDATE;

        if (!this.state.currentWorkflowState) {
            return null;
        }
        if (candidateLevelGrading) {
            const secondaryStatusText = tl(
                'grading.committees.selector.committee.gradingLevel.candidate'
            );
            secondaryStatus = (
                <p>
                    {secondaryStatusText}
                    &nbsp;
                    <a
                        href="javascript:void(0)"
                        onClick={this.openCandidateLevelHelp}
                    >
                        {tl('grading.confirmation.individual.how')}
                    </a>
                    &nbsp;
                </p>
            );
        } else if (assessment?.isGradingWorkflowsEnabledForTest) {
            if (this.state.currentWorkflowState === WorkflowState.NOT_FINISHED) {
                secondaryStatus = tl(`grading.header.secondaryStatus.${this.state.currentWorkflowState}`);
            } else {
                return null;
            }
        } else {
            switch (this.state.currentWorkflowState) {
            case WorkflowState.NOT_READY_FOR_GRADING:
            case WorkflowState.SINGLE_GRADER:
                return null;
            case WorkflowState.FINAL_GRADING_CONFLICTS:
            case WorkflowState.FINAL_GRADING_ALLOWED:
                const marksMode = getMarksModeForTestOrCommittee(selectedCommitteeId);
                if (marksMode === GRADING_MODE.COLLABORATIVE) {
                    secondaryStatus = tl(
                        'grading.header.secondaryStatus.collaborative'
                    );
                } else if (selectedCommitteeId > 0) {
                    secondaryStatus = tl(
                        `grading.header.secondaryStatus.${this.state.currentWorkflowState}.committee`
                    );
                } else {
                    secondaryStatus = tl(
                        `grading.header.secondaryStatus.${this.state.currentWorkflowState}.test`
                    );
                }
                break;
            default:
                secondaryStatus = tl(
                    `grading.header.secondaryStatus.${this.state.currentWorkflowState}`
                );
            }
        }
        return (
            <React.Fragment>
                <Breadcrumb.Section>{secondaryStatus}</Breadcrumb.Section>
                <Breadcrumb.Divider className={styles.divider}>
                    /
                </Breadcrumb.Divider>
            </React.Fragment>
        );
    };

    renderPrimaryStatus = () => {
        const { workflowInfo } = this.props.context;
        const { isGradingWorkflowsEnabledForTest } = this.props.assessment;
        const { moderationStatus, isModerator } = workflowInfo || {};

        if (!isGradingWorkflowsEnabledForTest) return getMarkingStatus(this.state.currentWorkflowState);
        if (isModerator) {
            if (moderationStatus === ModerationStatus.READY_FOR_MODERATION) {
                return tl('grading.header.moderation.reviewInProgress');
            }
            if (moderationStatus === ModerationStatus.SATISFACTORY
                || moderationStatus === ModerationStatus.UNSATISFACTORY) {
                return <span className={gradingStyles.moderationReviewCompleted}>{tl('grading.header.moderation.reviewCompleted')}</span>;
            }
        }
        return getMarkingStatusForGradingWorkflow(this.state.currentWorkflowState);
    };

    renderModerationCompletedStatus = () => {
        const { workflowInfo } = this.props.context;
        const { isGradingWorkflowsEnabledForTest } = this.props.assessment;
        const { moderationStatus } = workflowInfo || {};

        if (!isGradingWorkflowsEnabledForTest) return null;
        if (moderationStatus === ModerationStatus.SATISFACTORY) {
            return (
                <div className={gradingStyles.moderationResult}>
                    <Icon name="check circle outline" className={gradingStyles.moderationIconApproved} />
                    {tl('grading.header.moderation.approved')}
                </div>
            );
        }
        if (moderationStatus === ModerationStatus.UNSATISFACTORY) {
            return (
                <div className={gradingStyles.moderationResult}>
                    <Icon name="times circle outline" className={gradingStyles.moderationIconNotApproved} />
                    {tl('grading.header.moderation.notApproved')}
                </div>
            );
        }
        return null;
    };

    renderModerationActionButtons = () => {
        return (
            <Button.Group>
                <Button
                    basic
                    color="blue"
                    className={`${styles.moderationButton} ${styles.doNotApproveButton}`}
                    onClick={() => this.handleModerationAction(ModerationStatus.UNSATISFACTORY)}
                >
                    {tl('grading.header.moderation.doNotApprove')}
                </Button>
                <Button
                    primary
                    className={styles.moderationButton}
                    onClick={() => this.handleModerationAction(ModerationStatus.SATISFACTORY)}
                >
                    {tl('grading.header.moderation.approve')}
                </Button>
            </Button.Group>
        );
    };

    handleModerationAction = (moderationStatus: ModerationStatus) => {
        const actionName = moderationStatus === ModerationStatus.SATISFACTORY
            ? OptionsMenuActionsName.SET_MODERATION_STATUS_APPROVE
            : OptionsMenuActionsName.SET_MODERATION_STATUS_DO_NOT_APPROVE;
        store.dispatch(onToggleConfirmModal(actionName));
    };

    renderStatus = () => {
        const { assessment, context } = this.props;
        const { workflowInfo } = context;
        const { moderationStatus } = workflowInfo || {};
        const primaryStatus = this.renderPrimaryStatus();
        const isGradingWorkflowsEnabledForTest = assessment?.isGradingWorkflowsEnabledForTest;
        const isReadyForModeration = isGradingWorkflowsEnabledForTest && moderationStatus === ModerationStatus.READY_FOR_MODERATION;
        const isModerationCompleted = isGradingWorkflowsEnabledForTest && (moderationStatus === ModerationStatus.SATISFACTORY
            || moderationStatus === ModerationStatus.UNSATISFACTORY);

        return (
            <div className={styles.statusContainer}>
                <Breadcrumb
                    size="small"
                    className={`${styles.breadCrumb} no-padding-right`}
                >
                    <Breadcrumb.Section active className={styles.primaryStatus}>
                        <Header
                            color={getColor(
                                assessment.assessmentRunGradingStatus
                            )}
                            className={styles.gradingStatus}
                        >
                            {primaryStatus}
                            {!isModerationCompleted && this.renderNotSubmittedCandidatesWarning()}
                        </Header>
                    </Breadcrumb.Section>
                </Breadcrumb>

                {isModerationCompleted && this.renderModerationCompletedStatus()}
                {!isModerationCompleted && !isReadyForModeration && (
                    (
                        <Breadcrumb
                            size="small"
                            className={`${styles.breadCrumb} no-padding-right`}
                        >
                            {this.renderSecondaryStatus()}
                            <Breadcrumb.Section className={gradingStyles.alignTextRightInFlex}>
                                {this.getProgressStatus()}
                            </Breadcrumb.Section>
                        </Breadcrumb>
                    )
                )}
            </div>
        );
    };

    renderNotSubmittedCandidatesWarning = () => {
        const { context } = this.props;
        const { workflowInfo } = context;
        const {
            numberOfNotSubmittedCandidates,
            longestDeliveryDeadlineWithExtraTime,
        } = workflowInfo;
        if (numberOfNotSubmittedCandidates > 0) {
            const longestMoment = moment.utc(longestDeliveryDeadlineWithExtraTime, SIMPLE_DATE_FORMAT_STRING_NO_SECONDS);
            if (longestMoment.local().isAfter(moment())) {
                return (
                    <Popup
                        className={gradingStyles.notSubmittedCandidatePopup}
                        on="hover"
                        position="bottom center"
                        mouseEnterDelay={500}
                        mouseLeaveDelay={500}
                        trigger={(
                            <span className={gradingStyles.candidatesNotSubmittedWarning}>
                                <Icon name="info circle" className={gradingStyles.warningIcon} />
                                {tl('grading.header.notSubmittedCandidatesWarning.title')}
                            </span>
                        )}
                        hoverable
                    >
                        <div>
                            {tl('grading.header.notSubmittedCandidatesWarning.oneOrMoreExtraTime')}
                        </div>
                        <div>
                            {tl('grading.header.notSubmittedCandidatesWarning.completeList', {
                                maxExtraTimeExpiration: getFormattedDuration(moment(), longestMoment.local()),
                            })}
                        </div>
                    </Popup>
                );
            }
        }
        return null;
    }

    getExtraTimeOrIddCountdown = () => {
        const { workflowInfo } = this.props.context;
        const {
            numberOfNotSubmittedCandidates,
            longestDeliveryDeadlineWithExtraTime,
        } = workflowInfo;
        const longestMoment = moment.utc(longestDeliveryDeadlineWithExtraTime, SIMPLE_DATE_FORMAT_STRING_NO_SECONDS);
        const diff = moment().diff(
            longestMoment.local()
        );
        if (diff >= 0 || numberOfNotSubmittedCandidates === 0) {
            window.clearInterval(this.extraTimeOrIddIntervalId);
            this.extraTimeOrIddIntervalId = null;
            return null;
        }
        return getFormattedDuration(moment(), longestMoment.local());
    };

    getActiveAction = (actionKey: string) => {
        const menuItems = getOptionsMenuItems();
        const activeAction = menuItems
            .reduce((acc: MenuItemAction[], val: MenuItemsGroup) => acc.concat(val.actions), [])
            .find((action: MenuItemAction) => action.workflowAction === actionKey);
        return activeAction;
    };

    getActionHandlerFromKey = (actionKey: WorkflowPrimaryActions) => {
        const { context } = this.props;
        const { workflowInfo } = context;
        const { numberOfNotSubmittedCandidates } = workflowInfo;
        // handle warning for not submitted candidates
        if (numberOfNotSubmittedCandidates > 0
            && (actionKey === WorkflowPrimaryActions.SHARE_OWN_GRADES || actionKey === WorkflowPrimaryActions.CONFIRM_FINAL_GRADES)) {
            const activeAction = this.getActiveAction(actionKey);
            if (activeAction && activeAction.notSubmittedCandidatesWarning) {
                const { longestDeliveryDeadlineWithExtraTime } = workflowInfo;
                const longestDeliveryDeadlineMoment = moment.utc(longestDeliveryDeadlineWithExtraTime, SIMPLE_DATE_FORMAT_STRING_NO_SECONDS);
                if (longestDeliveryDeadlineMoment.local().isAfter(moment())) {
                    store.dispatch(onToggleConfirmModal(activeAction.actionName));
                    return;
                }
            }
        }

        if (actionKey === WorkflowPrimaryActions.CONFIRM_FINAL_GRADES && isGraderThatStillHasNotGradedCandidates()) {
            const activeAction = this.getActiveAction(actionKey);
            if (activeAction) {
                store.dispatch(onToggleConfirmModal(activeAction.actionName));
                return;
            }
        }
        store.dispatch(onPrimaryActionClick(actionKey));
    };

    renderPrimaryActionButton = () => {
        const { assessment, context } = this.props;
        const selectedCommitteeId = getPartyCommitteeIdForContext(context.partyState.gradingPartyDocument);
        const { workflowInfo } = context;
        const { evaluatorPrimaryAction, plannerPrimaryAction } = workflowInfo;
        const candidateLevelGrading: boolean = getGradingLevelForTestOrCommittee(selectedCommitteeId)
            === GRADING_LEVEL.CANDIDATE;

        if (
            (!evaluatorPrimaryAction && !plannerPrimaryAction)
            || (
                candidateLevelGrading
                && evaluatorPrimaryAction !== WorkflowPrimaryActions.DOWNLOAD_PROTOCOL
                && plannerPrimaryAction !== WorkflowPrimaryActions.DOWNLOAD_PROTOCOL
            )
        ) {
            return undefined;
        }

        const actionKey = evaluatorPrimaryAction || plannerPrimaryAction;
        const onPrimaryActionClick = () => this.getActionHandlerFromKey(actionKey as WorkflowPrimaryActions);

        if (assessment?.isGradingWorkflowsEnabledForTest && actionKey === WorkflowPrimaryActions.SHARE_OWN_GRADES) {
            return null;
        }

        return (
            <Button
                primary
                className={styles.button}
                onClick={onPrimaryActionClick}
            >
                {tl(`grading.header.primaryActions.${actionKey}`)}
            </Button>
        );
    };

    openPossibleIssuesOverlay = () => {
        const { openIssuesOverlay, assessment } = this.props;
        const { gradingReadyIssues } = assessment;

        openIssuesOverlay(gradingReadyIssues ? gradingReadyIssues.length : 0);
    };

    renderNotReadyForGradingIssuesButton = () => {
        const { assessment } = this.props;
        const { gradingReadyIssues } = assessment;
        const issuesLength = gradingReadyIssues ? gradingReadyIssues.length : 0;
        if (issuesLength > 0) {
            return (
                <Button basic onClick={this.openPossibleIssuesOverlay}>
                    <Icon name="flag outline" />
                    <strong>{issuesLength}</strong>
                </Button>
            );
        }
        return null;
    };

    onBackToOriginalTool = () => {
        const { context } = this.props;
        navigationListService.backToOriginalTool(context);
    };

    onGotIt = () => {
        checkFirstTimeViewLocalStorage.setFirstTimeView();
        this.setState({ canPopUpOpen: false });
    };

    shouldRenderExternalInfoButton = () => {
        const { assessment } = this.props;
        const { externalSystem, amqpEnabled } = assessment;
        return !isNullOrUndefined(externalSystem)
        && (externalSystem === ExternalSystem.FS
            || externalSystem === ExternalSystem.LADOK
            || externalSystem === ExternalSystem.PCG
            || (externalSystem === ExternalSystem.API && amqpEnabled));
    };

    renderBackToOriginalToolButton = () => {
        const { canPopUpOpen } = this.state;
        const isFirstTime = checkFirstTimeViewLocalStorage.isFirstTimeView();
        if (isFirstTime && canPopUpOpen) {
            return (
                <Popup
                    on="hover"
                    position="bottom right"
                    mouseEnterDelay={500}
                    mouseLeaveDelay={500}
                    trigger={(
                        <Button.Group basic size="small">
                            <Button
                                aria-label={tl('grading.header.toOriginalTool')}
                                aria-haspopup="true"
                                icon="exchange"
                                onClick={this.onBackToOriginalTool}
                            />
                        </Button.Group>
                    )}
                    hoverable
                >
                    <div className={styles.popupContentContainer}>
                        <div className={styles.iconContainer}>
                            <Icon name="exchange" size="huge" />
                        </div>
                        <h2>
                            {tl('grading.header.notReady')}
                        </h2>

                        <span className={styles.popupSubtitle}>
                            {tl('grading.header.oldgrading')}
                        </span>
                        <span
                            className={styles.gotIt}
                            onClick={this.onGotIt}
                        >
                            {tl('grading.header.gotIt')}
                        </span>
                        <span>
                            <a href={tl('grading.header.marking2.link.help')} target="_blank">
                                {tl('grading.header.mark2')}
                            </a>
                        </span>
                    </div>
                </Popup>
            );
        }
        return (
            <Button.Group basic size="small">
                <Button
                    aria-label={tl('grading.header.toOriginalTool')}
                    aria-haspopup="true"
                    icon="exchange"
                    onClick={this.onBackToOriginalTool}
                />
            </Button.Group>
        );
    };

    renderBtnGroups = () => {
        const { assessment, context } = this.props;
        const isPlanner = hasCurrentUserPlannerPrivilege();
        const shouldRenderExternalInfoButton = this.shouldRenderExternalInfoButton();
        const { workflowInfo } = context;
        const { moderationStatus, isModerator } = workflowInfo || {};
        const isGradingWorkflowsEnabledForTest = assessment?.isGradingWorkflowsEnabledForTest;
        const isModerationEnabled = Boolean((window as any).marketplaceProperties?.ENABLE_MODERATION);
        const isReadyForModeration = isGradingWorkflowsEnabledForTest && isModerationEnabled && isModerator
            && moderationStatus === ModerationStatus.READY_FOR_MODERATION;
        const isModerationCompleted = isGradingWorkflowsEnabledForTest && (moderationStatus === ModerationStatus.SATISFACTORY
            || moderationStatus === ModerationStatus.UNSATISFACTORY);

        const isMarkingLegacyNavigationDisabled = Boolean((window as any).marketplaceProperties?.DISABLE_MARKING_1_0_NAVIGATION_PATH);

        return (
            <div className={styles.buttonGroup}>
                {context.workflowInfo && !isModerationCompleted && (
                    isReadyForModeration
                        ? this.renderModerationActionButtons()
                        : this.renderPrimaryActionButton()
                )}
                {context.workflowInfo && isPlanner
                && this.renderNotReadyForGradingIssuesButton()}
                {shouldRenderExternalInfoButton && <ExternalInfoButton />}
                {context.workflowInfo && (
                    <OptionsMenuButton />
                )}

                {
                    !isMarkingLegacyNavigationDisabled && !shouldRestrictVisibilityOfGraderInfo() && this.renderBackToOriginalToolButton()
                }
            </div>
        );
    };

     detailsPanelBtn = () => {
         const { context, onToggleDetailsPanel } = this.props;

         return (
             <Button
                 className={styles.detailsPanelBtn}
                 onClick={onToggleDetailsPanel}
                 basic
                 aria-label={tl('grading.header.info')}
                 disabled={shouldRestrictVisibilityOfGraderInfo() || !hasAccessToEventLogsForUser()}
             >
                 <Icon name="info circle" />
                 {
                     context.showDetailsPanel
                         ? <Icon name="chevron right" />
                         : <Icon name="chevron left" />
                 }
             </Button>
         );
     };

     render() {
         const { context, session } = this.props;
         const { user: { isStudent } } = session;
         return (
             <div className={styles.headerRight}>
                 {!isStudent && context.workflowInfo && this.renderStatus()}
                 {!isStudent && this.renderBtnGroups()}
                 <UserIconButton />
                 {!isStudent && this.detailsPanelBtn()}
             </div>
         );
     }
}

export default HeaderRight;
